/**
 * CR-FoldLine: Fold Line Overlay for Live Preview
 * 
 * Renders a dotted horizontal line overlay at a specified height (400px or 600px)
 * to show where cards would be cropped in compact views.
 * 
 * This is an overlay-only component — it does not affect layout or clip content.
 */

import { cn } from "@/lib/utils";

export type FoldLineHeight = "off" | "normal" | "large";

export const FOLD_LINE_HEIGHTS: Record<Exclude<FoldLineHeight, "off">, number> = {
  normal: 400,
  large: 600,
};

interface FoldLineOverlayProps {
  /** Which fold line to show */
  foldHeight: FoldLineHeight;
  /** Additional classes for the container */
  className?: string;
}

/**
 * Renders a dotted fold line overlay at the specified height.
 * Must be placed inside a position: relative container.
 */
export function FoldLineOverlay({ foldHeight, className }: FoldLineOverlayProps) {
  if (foldHeight === "off") {
    return null;
  }
  
  const heightPx = FOLD_LINE_HEIGHTS[foldHeight];
  
  return (
    <div
      className={cn(
        "absolute left-0 right-0 pointer-events-none z-20",
        className
      )}
      style={{ top: `${heightPx}px` }}
    >
      {/* Dotted line - using accent/warning-style semantic colors */}
      <div className="w-full border-t-2 border-dashed border-accent-foreground/40" />
      
      {/* Label pill */}
      <div className="absolute right-2 -top-3 flex items-center">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent border border-accent-foreground/20 text-xs font-medium text-accent-foreground">
          Fold @ {heightPx}px
        </span>
      </div>
    </div>
  );
}

export default FoldLineOverlay;
